/* __next_internal_client_entry_do_not_use__ ImageUploaderDropzone auto */ import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _ts_generator } from "@swc/helpers/_/_ts_generator";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { formatBytes } from "@faire/web--source/common/formatBytes";
import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { backwardsCompatibleValues, useStrictLocalization } from "@faire/web--source/common/localization/localize";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import * as React from "react";
import Dropzone from "react-dropzone";
import { useIsSSR } from "../../common/server-side-rendering/isSSR";
import { ImageUploaderStatus } from "./ImageUploaderStatus";
import { DropzoneContainer, CentralIconWrapper, MaxSizeDiv } from "./styles";
var ACCEPTED_FILES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/webp",
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ".jpeg",
    ".jpg",
    ".png",
    ".webp",
    ".pdf",
    ".xlsx"
];
var MAX_FILE_SIZE = 10 * 1024 * 1024;
/**
 * @trackcomponent
 * @link [Docs](https://slate.faire.team/component/dropzone/web/dropzone)
 */ export var ImageUploaderDropzone = function(param) {
    var onImageUpload = param.onImageUpload, onUploadError = param.onUploadError, height = param.height, width = param.width, currentImage = param.currentImage, uploadCta = param.uploadCta, loading = param.loading, error = param.error, maxFileSize = param.maxFileSize, circular = param.circular, _param_externallyManagedReupload = param.externallyManagedReupload, externallyManagedReupload = _param_externallyManagedReupload === void 0 ? false : _param_externallyManagedReupload, displayRestrictions = param.displayRestrictions;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var maxFileSizeOrDefault = maxFileSize !== null && maxFileSize !== void 0 ? maxFileSize : MAX_FILE_SIZE;
    var showImage = !!currentImage && !loading;
    var showUploadError = !currentImage && !!error;
    var isSSR = useIsSSR();
    var iconColor;
    if (showImage) {
        iconColor = Core.icon.inverse;
    } else if (showUploadError) {
        iconColor = Core.icon.critical;
    } else {
        iconColor = Core.text.default;
    }
    var handleRejectedImage = function(rejectedFile) {
        var baseMessage = strictLocalize({
            id: "+jsFYt",
            defaultMessage: [
                {
                    type: 0,
                    value: "Error uploading "
                },
                {
                    type: 1,
                    value: "fileName"
                },
                {
                    type: 0,
                    value: "."
                }
            ]
        }, backwardsCompatibleValues({
            fileName: rejectedFile.file.name
        }));
        if (rejectedFile.file.size > maxFileSizeOrDefault) {
            onUploadError("".concat(baseMessage, " ").concat(strictLocalize({
                id: "UK+f1A",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Please upload a file less than "
                    },
                    {
                        type: 1,
                        value: "fileSize"
                    },
                    {
                        type: 0,
                        value: "."
                    }
                ]
            }, backwardsCompatibleValues({
                fileSize: formatBytes(maxFileSizeOrDefault, strictLocalize)
            }))));
        } else if (!ACCEPTED_FILES.includes(rejectedFile.file.type)) {
            onUploadError("".concat(baseMessage, " ").concat(strictLocalize({
                id: "pGrVzS",
                defaultMessage: [
                    {
                        type: 1,
                        value: "fileType"
                    },
                    {
                        type: 0,
                        value: " is not a supported file type."
                    }
                ]
            }, backwardsCompatibleValues({
                fileType: rejectedFile.file.type
            }))));
        } else {
            onUploadError("".concat(baseMessage, " ").concat(strictLocalize({
                id: "LXTNB8",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Please try again."
                    }
                ]
            })));
        }
    };
    var handleDrop = function(acceptedFiles, rejectedFiles) {
        acceptedFiles.forEach(/*#__PURE__*/ function() {
            var _ref = _async_to_generator(function(acceptedFile) {
                var e;
                return _ts_generator(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            _state.trys.push([
                                0,
                                2,
                                ,
                                3
                            ]);
                            return [
                                4,
                                onImageUpload(acceptedFile)
                            ];
                        case 1:
                            _state.sent();
                            return [
                                3,
                                3
                            ];
                        case 2:
                            e = _state.sent();
                            onUploadError(strictLocalize({
                                id: "0ZOu5r",
                                defaultMessage: [
                                    {
                                        type: 0,
                                        value: "Error uploading "
                                    },
                                    {
                                        type: 1,
                                        value: "fileName"
                                    },
                                    {
                                        type: 0,
                                        value: ". Please try again."
                                    }
                                ]
                            }, backwardsCompatibleValues(backwardsCompatibleValues({
                                fileName: acceptedFile.name
                            }))));
                            return [
                                3,
                                3
                            ];
                        case 3:
                            return [
                                2
                            ];
                    }
                });
            });
            return function(acceptedFile) {
                return _ref.apply(this, arguments);
            };
        }());
        rejectedFiles.forEach(function(rejectedFile) {
            return handleRejectedImage(rejectedFile);
        });
    };
    return /*#__PURE__*/ _jsx(Dropzone, {
        noClick: externallyManagedReupload && showImage,
        multiple: false,
        onDrop: handleDrop,
        accept: ACCEPTED_FILES,
        maxSize: maxFileSizeOrDefault,
        children: function(param) {
            var getRootProps = param.getRootProps, getInputProps = param.getInputProps;
            return /*#__PURE__*/ _jsx(DropzoneContainer, {
                $externallyManagedClicks: externallyManagedReupload && showImage,
                $height: height,
                $width: width,
                $uploadError: showUploadError,
                $circleImage: circular,
                children: /*#__PURE__*/ _jsxs(MaxSizeDiv, _object_spread_props(_object_spread({}, getRootProps()), {
                    children: [
                        /*#__PURE__*/ _jsx("label", {
                            htmlFor: "imageUploader",
                            style: {
                                display: "none"
                            },
                            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                id: "b4HW4N",
                                defaultMessage: [
                                    {
                                        type: 0,
                                        value: "Image Uploader"
                                    }
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx("input", _object_spread_props(_object_spread({}, getInputProps()), {
                            id: "imageUploader"
                        })),
                        showImage && currentImage ? /*#__PURE__*/ _jsx("img", {
                            src: optimizeImageUrl(currentImage, isSSR),
                            alt: currentImage.alt_text
                        }) : null,
                        /*#__PURE__*/ _jsx(CentralIconWrapper, {
                            $color: iconColor,
                            $defaultHidden: showImage,
                            children: /*#__PURE__*/ _jsx(ImageUploaderStatus, {
                                uploadCta: uploadCta,
                                isLoading: loading,
                                error: showUploadError,
                                displayedMaxFileSize: displayRestrictions ? maxFileSizeOrDefault : undefined,
                                displayedFileTypes: displayRestrictions ? strictLocalize({
                                    id: "yXXB1E",
                                    defaultMessage: [
                                        {
                                            type: 0,
                                            value: "JPEG, JPG, PNG, WEBP, PDF or XLSX"
                                        }
                                    ]
                                }) : undefined
                            })
                        })
                    ]
                }))
            });
        }
    });
};
